import React, { memo, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { OrderedSet } from 'immutable';

import { documentsByIdSelector } from '@/domain/documents';
import { bulkApprovalReplaceAction } from '@/domain/approvals';

import useDotsMenu from '@/pages/company/WorkspaceRoute/components/CompanyBulkPanel/DotsMenu/useDotsMenu';
import useToggle from '@/hooks/useToggle';
import DropdownButton from '@/components/mui/DropdownButton';

import MoreHorizOutlinedIcon from '@mui/icons-material/MoreHorizOutlined';
import DialogMoveDocumentsToCompany from '@/pages/common/Dialog/DialogMoveDocumentsToCompany';
import DialogManageBulkApprovalFlow from '@/pages/common/Dialog/DialogManageBulkApprovalFlow';
import DialogBulkReplaceApprover from '@/pages/common/Dialog/DialogBulkReplaceApprover';
import { Divider } from '@/pages/company/WorkspaceRoute/components/CompanyBulkPanel/StyledComponents';
import CONST from '@/domain/documents/constants';

interface DotsMenuProps {
  selectedIds: OrderedSet<string>;
  clearSelection: () => void;
}

const DotsMenu: React.FC<DotsMenuProps> = ({ selectedIds, clearSelection }) => {
  const dispatch = useDispatch();
  const documentsByID = useSelector(documentsByIdSelector);

  const [toggleMoveDialog, onToggleMoveDialog] = useToggle(false);
  const [toggleBulkApproval, onToggleBulkApproval] = useToggle(false);
  const [toggleReplaceApprover, onToggleReplaceApprover] = useToggle(false);

  const fromOptions = useMemo(() => {
    return documentsByID.reduce((acc, d) => {
      if (selectedIds.includes(d.documentID) && d.approvals.nodes && !d.tags.has(CONST.tags.approvalComplete)) {
        d.approvals.nodes.forEach(({ username }) => {
          acc.push(username);
        });
      }
      return [...new Set(acc)];
    }, []);
  }, [documentsByID, selectedIds]);

  const itemsOptions = {
    moveToItemOptions: {
      disabled: false,
      onClick: onToggleMoveDialog,
    },
    bulkApprovalItemOptions: {
      disabled: false,
      onClick: onToggleBulkApproval,
    },
    replaceApprover: {
      disabled: !fromOptions.length,
      onClick: onToggleReplaceApprover,
    },
  };

  const { menuOptions } = useDotsMenu({ selectedIds, clearSelection, itemsOptions });

  const handleBulkReplaceApproval = useCallback(
    (data) => {
      return new Promise((resolve, reject) => {
        dispatch(
          bulkApprovalReplaceAction({
            selectedIDs: selectedIds,
            replace: data.replace,
            to: data.to,
            resolve,
            reject,
          }),
        );
      }).then(() => {
        onToggleReplaceApprover();
        clearSelection();
      });
    },
    [dispatch, selectedIds, onToggleReplaceApprover, clearSelection],
  );

  return (
    <>
      {menuOptions.length ? (
        <>
          <Divider />
          <DropdownButton
            options={menuOptions}
            BtnIcon={<MoreHorizOutlinedIcon />}
            IconButtonProps={{ size: 'medium', color: 'default', sx: { '&:hover': { color: 'primary.main' } } }}
          />
          {toggleMoveDialog && (
            <DialogMoveDocumentsToCompany
              docIds={selectedIds}
              onClose={onToggleMoveDialog}
              clearSelection={clearSelection}
              selectedDocumentsCount={selectedIds.size}
            />
          )}
          {toggleBulkApproval && (
            <DialogManageBulkApprovalFlow
              open={toggleBulkApproval}
              onClose={onToggleBulkApproval}
              selectedIDs={selectedIds}
            />
          )}
          {toggleReplaceApprover && (
            <DialogBulkReplaceApprover
              open={toggleReplaceApprover}
              onSubmit={handleBulkReplaceApproval}
              onClose={onToggleReplaceApprover}
              fromOptions={fromOptions}
            />
          )}
        </>
      ) : null}
    </>
  );
};

export default memo(DotsMenu);
